import React, { type JSX } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useAppSelector } from "../../hooks/reduxHooks";
import { selectIsParagraphSelected } from "../../DraftSelectionSlice_Store";
import ParagraphContent from "./DraftParagraphContent";
import { BADGE_CONFIG } from "./draftPreviewTypes";
import type {
  AdvisorsTableData,
  ContactsTableData,
  Paragraph,
  ParagraphData,
  ParagraphItemProps,
  RecipientData,
  SignatureData,
} from "./draftPreviewTypes";

const PREVIEW_LENGTH = 100;

const TYPE_COLOURS: Record<string, string> = {
  markdown: "bg-blue-100 text-blue-700",
  recipients: "bg-purple-100 text-purple-700",
  signatures: "bg-green-100 text-green-700",
  advisors_table: "bg-orange-100 text-orange-700",
  contacts_table: "bg-orange-100 text-orange-700",
};

const PREVIEW_BUILDERS: Record<string, (data: ParagraphData) => string> = {
  recipients: (data) =>
    "For the attention of: " +
    ((data as RecipientData)?.recipients?.map((r) => r.name).join(", ") || "Recipients"),
  signatures: (data) =>
    "Signatures: " +
    ((data as SignatureData)?.signatories?.map((s) => s.name).join(", ") || "Signatures"),
  advisors_table: (data) => (data as AdvisorsTableData)?.title || "Advisors Table",
  contacts_table: (data) => (data as ContactsTableData)?.title || "Contacts Table",
};

/** One-line summary shown while a paragraph is collapsed. */
function previewText(paragraphType: string, data: ParagraphData): string {
  if (paragraphType === "markdown") {
    const text = typeof data === "string" ? data : String(data || "");
    return text.length > PREVIEW_LENGTH ? text.substring(0, PREVIEW_LENGTH) + "..." : text;
  }

  const build = PREVIEW_BUILDERS[paragraphType];
  // The declared union is narrower than what the drafter actually emits
  // (page_break, for one), so the fallback below stays reachable.
  return build ? build(data) : `${paragraphType} content`;
}

/** Section chip. */
function SectionBadge({ section }: { section: string }): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-teal-100 text-teal-700">
      {section}
    </span>
  );
}

/** Paragraph type chip, colour-coded per known type. */
function TypeBadge({ type }: { type: string }): JSX.Element {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_COLOURS[type] || "bg-gray-100 text-gray-700"}`}
    >
      {type}
    </span>
  );
}

/** Style chip for paragraphs that declare one. */
function StyleBadge({ style }: { style: string }): JSX.Element {
  return (
    <span className="text-xs px-2 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
      {style}
    </span>
  );
}

/** The badge row; every badge is behind its own BADGE_CONFIG flag. */
function ParagraphBadges({
  index,
  paragraphType,
  paragraph,
}: {
  index: number;
  paragraphType: string;
  paragraph: Paragraph;
}): JSX.Element {
  const { style, section_id, fixed } = paragraph;
  return (
    <div className="flex items-center space-x-2">
      {BADGE_CONFIG.showIndexBadge && (
        <span className="text-xs font-medium text-gray-500">#{index}</span>
      )}
      {BADGE_CONFIG.showSectionBadge && section_id && <SectionBadge section={section_id} />}
      {BADGE_CONFIG.showTypeBadge && <TypeBadge type={paragraphType} />}
      {BADGE_CONFIG.showStyleBadge && style && <StyleBadge style={style} />}
      {BADGE_CONFIG.showFixedBadge && fixed && (
        <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">fixed</span>
      )}
    </div>
  );
}

/** Selection checkbox; the wrapper takes the click so the row does not toggle. */
function SelectionCheckbox({
  paragraphId,
  isSelected,
  onClick,
}: {
  paragraphId: number;
  isSelected: boolean;
  onClick: (e: React.MouseEvent) => void;
}): JSX.Element {
  return (
    <div className="flex-shrink-0 p-1 -m-1 cursor-pointer" onClick={onClick}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {}}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
        title={`Select paragraph #${paragraphId}`}
        style={{ pointerEvents: "none" }}
      />
    </div>
  );
}

/** Collapsible, selectable paragraph row. */
export default function ParagraphItem({
  paragraph,
  index,
  isExpanded,
  onToggle,
  projectId,
  docId,
  onToggleSelection,
}: ParagraphItemProps): JSX.Element {
  const { type, data } = paragraph;
  const paragraphType = type || "markdown";
  const paragraphId = index;

  // Get selection state from Redux
  const isSelected = useAppSelector(
    selectIsParagraphSelected(projectId, docId, String(paragraphId))
  );

  const handleCheckboxClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    onToggleSelection(paragraphId, isSelected);
  };

  return (
    <div className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}>
      <div className="flex items-start p-4">
        <SelectionCheckbox
          paragraphId={paragraphId}
          isSelected={isSelected}
          onClick={handleCheckboxClick}
        />

        <div onClick={onToggle} className="flex-shrink-0 cursor-pointer mx-3">
          {isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div onClick={onToggle} className="cursor-pointer mb-2">
            <ParagraphBadges index={index} paragraphType={paragraphType} paragraph={paragraph} />
          </div>

          {!isExpanded ? (
            <div onClick={onToggle} className="text-sm text-gray-600 truncate cursor-pointer">
              {previewText(paragraphType, data)}
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <ParagraphContent paragraph={paragraph} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
